import { Controller } from "@hotwired/stimulus"

interface CloudinaryUploadInfo {
  public_id: string
  secure_url: string
  resource_type: "image" | "video" | "raw"
  format: string
  bytes: number
}

interface CloudinaryWidgetResult {
  event: string
  info: CloudinaryUploadInfo
}

interface CloudinaryWidget {
  open(): void
}

declare const cloudinary: {
  createUploadWidget(
    options: Record<string, unknown>,
    callback: (error: unknown, result: CloudinaryWidgetResult) => void
  ): CloudinaryWidget
}

// Single-image Cloudinary upload for the brand logo. Writes the resulting
// secure_url into a hidden form input; renders a thumbnail next to the
// upload button so the admin can see what's currently attached.
export default class extends Controller {
  static targets = ["urlInput", "preview", "openButton"]
  static values = {
    cloudName: String,
    apiKey: String,
    folder: String,
    signatureUrl: String
  }

  declare readonly urlInputTarget: HTMLInputElement
  declare readonly previewTarget: HTMLImageElement
  declare readonly openButtonTarget: HTMLButtonElement
  declare cloudNameValue: string
  declare apiKeyValue: string
  declare folderValue: string
  declare signatureUrlValue: string

  private widget: CloudinaryWidget | null = null

  open(event: Event): void {
    event.preventDefault()
    this.ensureWidget().open()
  }

  private ensureWidget(): CloudinaryWidget {
    if (this.widget) return this.widget
    this.widget = cloudinary.createUploadWidget(
      {
        cloudName: this.cloudNameValue,
        apiKey: this.apiKeyValue,
        folder: this.folderValue,
        uploadPrefix: "https://api-eu.cloudinary.com/",
        sources: ["local", "url"],
        multiple: false,
        clientAllowedFormats: ["png", "jpg", "jpeg", "webp", "svg"],
        maxFileSize: 5_000_000,
        uploadSignature: this.signRequest.bind(this),
        cropping: false,
        showPoweredBy: false
      },
      (error, result) => {
        if (error) {
          console.error("Cloudinary upload failed:", error)
          return
        }
        if (result.event === "success") {
          this.urlInputTarget.value = result.info.secure_url
          this.previewTarget.src = result.info.secure_url
          this.previewTarget.classList.remove("hidden")
          this.openButtonTarget.textContent = "Replace logo"
        }
      }
    )
    return this.widget
  }

  private signRequest(
    callback: (signature: string) => void,
    paramsToSign: Record<string, unknown>
  ): void {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content
    fetch(this.signatureUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ params_to_sign: paramsToSign })
    })
      .then(response => response.text())
      .then(signature => callback(signature))
  }
}
